import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories, seoArticles } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileArticleOpen, setMobileArticleOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navbarBg = scrolled
    ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
    : 'bg-black/30 backdrop-blur-sm';

  const textColor = scrolled
    ? 'text-foreground-900 hover:text-foreground-700'
    : 'text-white hover:text-white/80';

  const textColorMuted = scrolled
    ? 'text-foreground-700 hover:text-foreground-900'
    : 'text-white/90 hover:text-white';

  const mobileBtnColor = scrolled ? 'text-foreground-900' : 'text-white';

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${navbarBg}`}>
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className={`font-heading text-xl md:text-2xl font-bold tracking-tight ${scrolled ? 'text-foreground-900' : 'text-white'}`}>
              Kakobuy
            </span>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${textColorMuted}`}
            >
              {t('nav_home')}
            </Link>

            {/* Categories dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${textColorMuted}`}
              >
                {t('nav_categories')}
                <span className={`w-3.5 h-3.5 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-lg border border-background-200/70 py-2 max-h-[420px] overflow-y-auto"
                  style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }}
                >
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0">
                        <img src={cat.image} alt={cat.namePl} className="w-full h-full object-cover" />
                      </div>
                      <span>{cat.namePl}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${textColorMuted}`}
            >
              {t('nav_guides')}
            </a>

            <Link
              to="/#artykuly"
              className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${textColorMuted}`}
            >
              {t('nav_blog')}
            </Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-accent-500 text-white text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
            >
              {t('btn_buy_now')}
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${mobileBtnColor}`}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-3 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                <i className="ri-home-4-line text-sm"></i>
              </span>
              {t('nav_home')}
            </Link>

            {/* Mobile categories expandable */}
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <span className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600">
                  <i className="ri-grid-line text-sm"></i>
                </span>
                {t('nav_categories')}
              </span>
              <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-foreground-400"></i>
              </span>
            </button>
            {mobileCatOpen && (
              <div className="ml-4 border-l-2 border-background-200/70 pl-4 mt-1 mb-2 grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-2 py-2 rounded-lg text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-md overflow-hidden shrink-0">
                      <img src={cat.image} alt={cat.namePl} className="w-full h-full object-cover" />
                    </div>
                    <span className="leading-tight">{cat.namePl}</span>
                  </a>
                ))}
              </div>
            )}

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className="ri-book-open-line text-sm"></i>
              </span>
              {t('nav_guides')}
            </a>

            {/* Mobile articles expandable */}
            <button
              onClick={() => setMobileArticleOpen(!mobileArticleOpen)}
              className="w-full flex items-center justify-between px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <span className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-article-line text-sm"></i>
                </span>
                {t('nav_blog')}
              </span>
              <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileArticleOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-foreground-400"></i>
              </span>
            </button>
            {mobileArticleOpen && (
              <div className="ml-4 border-l-2 border-background-200/70 pl-4 mt-1 mb-2 flex flex-col gap-1">
                {seoArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/artykul/${article.slug}`}
                    className="flex items-center gap-2 px-2 py-2 rounded-lg text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded bg-background-200/50 text-foreground-400 shrink-0">
                      <i className="ri-file-text-line text-xs"></i>
                    </span>
                    <span className="leading-tight line-clamp-1">{article.title}</span>
                  </Link>
                ))}
              </div>
            )}

            <div className="border-t border-background-200/70 my-2"></div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="block mx-2 py-3 rounded-full bg-accent-500 text-white text-sm font-medium text-center whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('btn_buy_now')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}